import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { Member, UserPermissions } from "common/types";

import type { AgencyClientSchemaType } from "../schema";

async function addAgencyClientRequest(
  workspaceId: string,
  client: AgencyClientSchemaType,
  permissions: UserPermissions,
): Promise<Member> {
  const { firstName, lastName, email, skipLoginEmail } = client;

  return (
    await post(`workspaces/${workspaceId}/clients`, {
      first_name: firstName,
      last_name: lastName,
      email,
      permissions,
      skip_login_email: skipLoginEmail,
    })
  ).member;
}

export default function useAgencyClientsMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { membersKeys } = getQueryKeys(workspaceId);

  const {
    mutateAsync: addAgencyClient,
    isPending: isAddingAgencyClient,
    error: addAgencyClientError,
    reset: resetAgencyClientError,
  } = useMutation({
    mutationFn: ({
      client,
      permissions,
    }: {
      client: AgencyClientSchemaType;
      permissions: UserPermissions;
    }) => addAgencyClientRequest(workspaceId, client, permissions),
    onSuccess: (newMember) => {
      queryClient.setQueryData<Member[]>(membersKeys.list, (prevMembers) => [
        newMember,
        ...prevMembers,
      ]);
    },
  });

  return {
    addAgencyClient,
    isAddingAgencyClient,
    addAgencyClientError,
    resetAgencyClientError,
  };
}
